import React, { useState, useEffect } from "react";
import {
  findMatchingBudgetEntries,
  updateBudgetEntryStatus,
  calculateDueDate,
} from "../services/azureBudgetService";

const BookingMatchingPopup = ({ bookings, onClose, onMatch }) => {
  const [currentBookingIndex, setCurrentBookingIndex] = useState(0);
  const [matchingSuggestions, setMatchingSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [skippedBookings, setSkippedBookings] = useState([]);

  useEffect(() => {
    if (bookings && bookings.length > 0) {
      loadMatchingSuggestions(bookings[currentBookingIndex]);
    }
  }, [currentBookingIndex, bookings]);

  const loadMatchingSuggestions = async (booking) => {
    setLoading(true);
    try {
      const suggestions = await findMatchingBudgetEntries(booking);
      setMatchingSuggestions(suggestions);
    } catch (error) {
      console.error("Fehler beim Laden der Vorschläge:", error);
      setMatchingSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleMatch = async (budgetEntry) => {
    const currentBooking = bookings[currentBookingIndex];

    try {
      setLoading(true);

      // Aktualisiere Budget-Eintrag Status
      await updateBudgetEntryStatus(budgetEntry.id, "paid", {
        paid_date: currentBooking.Valuta,
        matched_booking_id: currentBooking.Nr,
      });

      // Callback zur Info
      if (onMatch) {
        onMatch({
          booking: currentBooking,
          budgetEntry,
        });
      }

      // Gehe zum nächsten Booking
      proceedToNext();
    } catch (error) {
      console.error("Fehler beim Matching:", error);
      alert("Fehler beim Zuordnen der Buchung.");
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    setSkippedBookings([...skippedBookings, bookings[currentBookingIndex]]);
    proceedToNext();
  };

  const proceedToNext = () => {
    if (currentBookingIndex < bookings.length - 1) {
      setCurrentBookingIndex(currentBookingIndex + 1);
    } else {
      // Alle Buchungen durchgearbeitet
      onClose();
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("de-CH", {
      style: "currency",
      currency: "CHF",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  if (!bookings || bookings.length === 0) {
    return null;
  }

  const currentBooking = bookings[currentBookingIndex];
  const progress = ((currentBookingIndex + 1) / bookings.length) * 100;

  return (
    <div className="booking-matching-overlay">
      <div className="booking-matching-popup">
        <div className="popup-header">
          <h3>Zahlung zuordnen</h3>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <p className="progress-text">
            Buchung {currentBookingIndex + 1} von {bookings.length}
          </p>
        </div>

        <div className="booking-details">
          <h4>Eingehende Zahlung:</h4>
          <div className="booking-info">
            <div className="info-row">
              <span className="label">Datum:</span>
              <span className="value">
                {new Date(currentBooking.Valuta).toLocaleDateString("de-CH")}
              </span>
            </div>
            <div className="info-row">
              <span className="label">Betrag:</span>
              <span className="value amount">
                {formatCurrency(Math.abs(currentBooking.Betrag))}
              </span>
            </div>
            <div className="info-row">
              <span className="label">Text:</span>
              <span className="value">{currentBooking.Buchungstext}</span>
            </div>
            <div className="info-row">
              <span className="label">Konto:</span>
              <span className="value">
                {currentBooking.Soll} / {currentBooking.Haben}
              </span>
            </div>
          </div>
        </div>

        {loading && <div className="matching-loading">Suche Zuordnungen...</div>}

        {!loading && matchingSuggestions.length > 0 && (
          <div className="matching-suggestions">
            <h4>Passende erwartete Einnahmen:</h4>
            <div className="suggestions-list">
              {matchingSuggestions.map((suggestion) => (
                <div key={suggestion.id} className="suggestion-item">
                  <div className="suggestion-details">
                    <div className="suggestion-name">{suggestion.name}</div>
                    <div className="suggestion-meta">
                      <span>
                        Fällig am:{" "}
                        {(calculateDueDate(suggestion) || new Date(suggestion.wann)).toLocaleDateString("de-CH")}
                      </span>
                      <span className="suggestion-amount">
                        {formatCurrency(
                          suggestion.brutto_betrag || suggestion.betrag
                        )}
                      </span>
                    </div>
                    {suggestion.projekt && (
                      <div className="suggestion-project">
                        Projekt: {suggestion.projekt}
                      </div>
                    )}
                  </div>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => handleMatch(suggestion)}
                    disabled={loading}
                  >
                    Zuordnen
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {!loading && matchingSuggestions.length === 0 && (
          <div className="no-matches">
            <p>Keine passenden erwarteten Einnahmen gefunden.</p>
            <p className="hint">
              Möglicherweise wurde diese Zahlung bereits zugeordnet oder es
              existiert keine entsprechende Budgetposition.
            </p>
          </div>
        )}

        <div className="popup-actions">
          <button
            className="btn btn-secondary"
            onClick={handleSkip}
            disabled={loading}
          >
            Später
          </button>
          <button
            className="btn btn-secondary"
            onClick={onClose}
            disabled={loading}
          >
            Abbrechen
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookingMatchingPopup;
